import { useDialog } from '@/common/dialog/DialogContext';
import { EntitySelectDialog } from '@/common/dialog/EntitySelectDialog';
import { Grid } from '@/common/grid/Grid';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';

const EntitySelectField = ({ label, module, entity, value, onChange }) => {
  const { openDialog } = useDialog();

  const handleClick = () => {
    if (value) {
      onChange(null);
      return;
    }
    openDialog(EntitySelectDialog, {
      data: { module, entity },
    }).then((result) => {
      const sel = result?.selectedEntity;
      if (!sel) return;
      onChange({ id: sel.id, code: sel.code, name: sel.name });
    });
  };

  const inputText = value ? value.code + (value.name ? ' - ' + value.name : '') : '';

  return (
    <TextField
      label={label}
      variant="outlined"
      size="small"
      fullWidth
      value={inputText}
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClick}>
                <FontAwesomeIcon icon={value ? fas.faTimes : fas.faSearch} />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export const PriceResLookup = () => {
  const rsrc = useRsrc();
  const gridRef = useRef();

  const [cust, setCust] = useNavStateSync('priceResLookupCust', null);
  const [prod, setProd] = useNavStateSync('priceResLookupProd', null);
  const [prog, setProg] = useNavStateSync('priceResLookupProg', null);
  const [date, setDate] = useNavStateSync('priceResLookupDate', null);
  const [searchParams, setSearchParams] = useNavStateSync('priceResLookupSearchParams', null);
  const [data, setData] = useState(null);
  const [isGridReady, setIsGridReady] = useState(false);

  const fieldDefs = [
    {
      field: 'custCode',
      displayName: 'Cust',
      cellRenderer: 'hyperlinkCellRenderer',
      module: 'md',
      entity: 'CUST',
      hasName: true,
    },
    {
      field: 'prodId',
      displayName: 'Product',
      cellRenderer: 'hyperlinkCellRenderer',
      module: 'md',
      entity: 'PROD',
    },
    { field: 'progId', displayName: 'Prog', cellRenderer: 'hyperlinkCellRenderer', module: 'md', entity: 'PROG' },
    {
      field: 'progProdId',
      displayName: 'Prog Price',
      cellRenderer: 'hyperlinkCellRenderer',
      module: 'md',
      entity: 'PROG_PROD',
    },
    { field: 'progType', displayName: 'Program Type' },
    { field: 'startDate', displayName: 'Start Date (MM/DD/YYYY)', cellRendererCode: 'DATE' },
    { field: 'endDate', displayName: 'End Date (MM/DD/YYYY)', cellRendererCode: 'DATE' },
    {
      field: 'tierNum',
      displayName: 'Active Tier',
      type: 'PRICE_RES_TIER',
      cellRendererCode: 'PRICE_RES_TIER',
      filter: 'agNumberColumnFilter',
      idField: 'activeTierId',
      module: 'md',
      entity: 'PROG_PROD_TIER',
      decimalPrecision: 0,
      minDecimalPrecision: 0,
    },
    {
      field: 'value',
      displayName: 'Concession',
      type: 'PROG_PROD_CALC_VALUE',
    },
    {
      field: 'pymtTerms',
      displayName: 'Pymt Terms',
      type: 'NUMBER',
      filter: 'agNumberColumnFilter',
      decimalPrecision: 0,
      minDecimalPrecision: 0,
    },
    { field: 'dateBasis', displayName: 'Date Basis' },
  ];

  const canSearch = !!cust && !!prod && !!date;

  const handleSearch = () => {
    setSearchParams({ custId: cust?.id, prodId: prod?.id, progId: prog?.id, date });
  };

  useEffect(() => {
    if (!searchParams) return;
    rsrc.data('md', 'PRICE_RES_LOOKUP_DATA_PLUGIN', searchParams).then(setData);
  }, [searchParams]);

  useEffect(() => {
    if (isGridReady && gridRef.current && data) {
      gridRef.current.setDataSource(data);
    }
  }, [isGridReady, data]);

  const onGridReady = () => {
    setIsGridReady(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          },
          gap: 2,
          alignItems: 'end',
          p: 2,
        }}
      >
        <EntitySelectField label="Customer" module="md" entity="CUST" value={cust} onChange={setCust} />
        <EntitySelectField label="Product" module="md" entity="SKU_PROD" value={prod} onChange={setProd} />
        <EntitySelectField label="Program" module="md" entity="PROG" value={prog} onChange={setProg} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date"
            value={date ? dayjs(String(date), 'YYYYMMDD') : null}
            onChange={(val) => setDate(val ? Number(val.format('YYYYMMDD')) : null)}
            desktopModeMediaQuery="@media (min-width: 0px)"
            slotProps={{
              actionBar: { actions: ['clear'] },
              textField: { variant: 'outlined', size: 'small', fullWidth: true },
            }}
          />
        </LocalizationProvider>
        <Button variant="contained" onClick={handleSearch} disabled={!canSearch} sx={{ height: 40 }}>
          Search
        </Button>
      </Box>
      {!canSearch && (
        <Box sx={{ px: 2, pb: 1, color: 'text.secondary', fontSize: 14 }}>
          Select a Customer, a Product and a Date to search.
        </Box>
      )}
      {data && (
        <Grid
          ref={gridRef}
          gridOptions={{ fieldDefs, rowModelType: 'clientSide', statusBar: null }}
          events={{ onGridReady }}
        />
      )}
    </Box>
  );
};
